import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { apolloHooks } from '@nxte-nl/apollo-hooks';

import { defaultUserPreFillData } from '../../constants/account';
import { checkUserExist } from '../../queries/checkUserExist.gql';
import { getUserInfo } from '../../queries/getUserInfo.gql';
import {
  AccountMessages,
  BasketMessages,
  GlobalMessages,
  RedeemMessages,
} from '../../types/Messages';
import { ModalTypes } from '../../types/Modals';
import { BaseResponse } from '../../types/apiCompoundCommon';
import {
  CustomerProfile,
  OrderProfile,
  QuerycheckUserExistArgs,
  StatusResponse,
  UserResponse,
} from '../../types/apiTypes';
import {
  beforeUnloadManager,
} from '../../utils/beforeUnloadManager/beforeUnloadManager';
import { redeemUtils } from '../../utils/redeemUtils/redeemUtils';
import { strings } from '../../utils/strings/strings';
import { messageHooks } from '../messageHooks/messageHooks';
import { modalHooks } from '../modalHooks/modalHooks';
import { navigationHooks } from '../navigationHooks/navigationHooks';
import { redeemHooks } from '../redeemHooks/redeemHooks';
import { routerBuildHooks } from '../routerBuildHooks/routerBuildHooks';

export const emptyUser: UserResponse = {
  errors: [],
  user: null,
};

export type IdentityUserHandlersParams = {
  handleSuccessCallback?: () => void;
  isRedeem?: boolean;
};

type UserAccountData = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  userData?: CustomerProfile | OrderProfile;
};

export const accountHooks = {
  useIdentifyUserHandlers({
    isRedeem,
    handleSuccessCallback,
  }: IdentityUserHandlersParams) {
    const userInfo = messageHooks.useSubjectState(AccountMessages.AccountUserInfo);
    const shouldAddPhysicalAddress =
      messageHooks.useSubjectState(RedeemMessages.ShouldAddPhysicalAddress);
    const { orderModalAuthMode } = messageHooks.useSubjectState(GlobalMessages.FeatureToggles);

    const checkoutPageRedirect = navigationHooks.useCheckoutPageRedirect();
    const redeemCheckoutPageRedirect = navigationHooks.useRedeemCheckoutPageRedirect();

    const checkoutRedirectHandler = routerBuildHooks.useRedirect(checkoutPageRedirect);
    const redeemCheckoutRedirectHandler = routerBuildHooks.useRedirect(redeemCheckoutPageRedirect);

    const redeemToken = redeemHooks.useGetRedeemToken();
    const setRedeemTokenSessionStorage = redeemUtils.setRedeemToken;

    const {
      onModalOpen: onModalOpenAccountIdentify,
      onModalClose: onModalCloseAccountIdentify,
      isOpenModal: isOpenModalAccountIdentify,
    } = modalHooks.useModal(ModalTypes.AccountIdentifyModal);

    const {
      onModalOpen: onModalOpenBasketGuestOrder,
    } = modalHooks.useModal(ModalTypes.BasketGuestOrderModal);

    const { handleOpenGuestDeliveryModal } = redeemHooks.useRedeemGuestDeliveryModal();

    const handleCloseModal = React.useCallback(() => {
      onModalCloseAccountIdentify();
    }, [onModalCloseAccountIdentify]);

    const isModalOpen = React.useMemo(
      () => isOpenModalAccountIdentify(),
      [isOpenModalAccountIdentify],
    );

    const handleAuthSuccess = React.useCallback(
      (userResponse?: UserResponse) => {
        if (!isRedeem) {
          if (handleSuccessCallback) {
            handleSuccessCallback();
          }
          checkoutRedirectHandler();
          return;
        }

        const user = userInfo?.user || userResponse?.user;
        if (user?.mainProfile.address?.postCode || !shouldAddPhysicalAddress) {
          setRedeemTokenSessionStorage(redeemToken);
          beforeUnloadManager.detach();

          redeemCheckoutRedirectHandler();
          return;
        }

        handleOpenGuestDeliveryModal();
      },
      [
        isRedeem,
        userInfo?.user,
        shouldAddPhysicalAddress,
        handleOpenGuestDeliveryModal,
        handleSuccessCallback,
        checkoutRedirectHandler,
        redeemCheckoutRedirectHandler,
        setRedeemTokenSessionStorage,
        redeemToken,
      ],
    );

    const handleOrderAsGuest = React.useCallback(() => {
      if (isRedeem) {
        return handleOpenGuestDeliveryModal();
      }
      onModalOpenBasketGuestOrder();
    }, [isRedeem, handleOpenGuestDeliveryModal, onModalOpenBasketGuestOrder]);

    const handleTryIdentifyUser = React.useCallback(() => {
      if (userInfo?.user) {
        return handleAuthSuccess();
      }

      if (orderModalAuthMode) {
        return onModalOpenAccountIdentify();
      }

      handleOrderAsGuest();
    }, [
      userInfo,
      orderModalAuthMode, handleOrderAsGuest, handleAuthSuccess, onModalOpenAccountIdentify]);

    return {
      isModalOpen,
      handleCloseModal,
      handleTryIdentifyUser,
      handleAuthSuccess,
      handleOrderAsGuest,
    };
  },
  useHandleResponseIdentifyUserCallback(isRedeem?: boolean, isModal?: boolean) {
    const navigateAccountLogin = navigationHooks.useAccountLoginRedirect();
    const navigateAccountRegister = navigationHooks.useAccountRegisterRedirect();

    const redirectHandler = routerBuildHooks.useRedirectCallback();

    const {
      onModalOpen: onModalOpenLogin,
    } = modalHooks.useModal(ModalTypes.LoginModal);
    const {
      onModalOpen: onModalOpenBasketGuestOrder,
    } = modalHooks.useModal(ModalTypes.BasketGuestOrderModal);
    const { handleOpenGuestDeliveryModal } = redeemHooks.useRedeemGuestDeliveryModal();

    const handleResponse = React.useCallback((response: BaseResponse<StatusResponse> | null) => {
      if (response === null || !response.data) {
        return;
      }

      if (response.data.status) {
        return isModal ? onModalOpenLogin() : redirectHandler(navigateAccountLogin());
      }

      if (isModal) {
        return isRedeem
          ? handleOpenGuestDeliveryModal()
          : onModalOpenBasketGuestOrder();
      }

      redirectHandler(navigateAccountRegister());
    }, [
      isModal,
      navigateAccountRegister,
      onModalOpenLogin,
      navigateAccountLogin,
      isRedeem,
      handleOpenGuestDeliveryModal,
      onModalOpenBasketGuestOrder,
      redirectHandler,
    ]);
    return { handleResponse };
  },
  useIdentifyUser(isRedeem?: boolean, isModal?: boolean) {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const setAccountUserPreFillData = messageHooks.useNext(AccountMessages.AccountUserPreFillData);

    const { handleResponse } = accountHooks.
      useHandleResponseIdentifyUserCallback(isRedeem, isModal);

    const [executeRequest, { loading }] = apolloHooks.
      useLazyQuery<BaseResponse<StatusResponse>,
      Pick<QuerycheckUserExistArgs, 'captchaToken' | 'email'>>(
        checkUserExist, {
          onCompleted: handleResponse,
        },
      );

    const handleIdentifyUser = React.useCallback(async (email: string) => {
      if (executeRecaptcha) {
        const captchaToken = await executeRecaptcha(CONFIG.reCaptchaBalanceCheckAction);

        const params = captchaToken && email
          ? {
            captchaToken,
            email,
          }
          : null;

        if (params) {
          setAccountUserPreFillData({ ...defaultUserPreFillData, email });
          executeRequest({
            variables: params,
          });
        }
      }
    }, [executeRecaptcha, executeRequest, setAccountUserPreFillData]);

    return {
      handleIdentifyUser,
      handleResponse,
      loading,
    };
  },

  useResetAccount() {
    const setUserInfo = messageHooks.useNext(AccountMessages.AccountUserInfo);
    const setForgotPasswordEmail = messageHooks.useNext(AccountMessages.AccountForgotPasswordEmail);
    const setUserPreFillData = messageHooks.useNext(AccountMessages.AccountUserPreFillData);
    const setAccountOrdersSplittedPerYear =
      messageHooks.useNext(AccountMessages.AccountOrdersSplittedPerYear);
    const setCart = messageHooks.useNext(BasketMessages.Cart);

    return React.useCallback(() => {
      setUserInfo(emptyUser);
      setForgotPasswordEmail('');
      setUserPreFillData(defaultUserPreFillData);
      setAccountOrdersSplittedPerYear(null);
      setCart(null);
    }, [
      setUserInfo,
      setForgotPasswordEmail,
      setUserPreFillData,
      setAccountOrdersSplittedPerYear,
      setCart,
    ]);
  },
  useHandleResponseAccountUserInfoCallback(callback?: (userResponse: UserResponse) => void) {
    const setUserInfo = messageHooks.useNext(AccountMessages.AccountUserInfo);
    const setUserInfoLoading = messageHooks.useNext(AccountMessages.AccountUserInfoLoading);
    const handleResponse = React.useCallback((response: BaseResponse<UserResponse> | null) => {
      if (response === null || !response.data) {
        return;
      }

      if (response.data.user) {
        callback && callback(response.data);
        setUserInfo(response.data);
        setUserInfoLoading(false);
      }
    }, [setUserInfo, callback, setUserInfoLoading]);
    return { handleResponse };
  },
  useSetAccountUserInfo(callback?: (userResponse: UserResponse) => void) {
    const { handleResponse } = accountHooks.useHandleResponseAccountUserInfoCallback(callback);

    const [executeRequest, { loading }] = apolloHooks.useLazyQuery<
    BaseResponse<UserResponse> | null>(getUserInfo,
      {
        onCompleted: handleResponse,
      });

    const handleSetAccountUserInfo = React.useCallback(() => {
      executeRequest();
    }, [executeRequest]);

    return {
      handleResponse,
      handleSetAccountUserInfo,
      userInfoLoading: loading,
    };
  },

  useBillingEmail(): string | null {
    const userData = messageHooks.useSubjectState(AccountMessages.AccountUserInfo);
    return React.useMemo(() => {
      if (userData === null
        || userData.user === null) {
        return null;
      }

      const { mainProfile } = userData.user;

      return mainProfile.invoiceEmail === null ? mainProfile.email : mainProfile.invoiceEmail;
    }, [userData]);
  },

  useIsPathToRouteMatchedHandler() {
    return React.useCallback((pathname: string, routes: string[]) => {
      for (const route of routes) {
        const regexRoute = route
          .replace(':language({languageParamRegex})?/', '(?:[a-z]{2}/)?')
          .replace(/\//g, '\\/');

        const regex = new RegExp('^' + regexRoute + '$');

        if (regex.test(pathname)) {
          return true;
        }
      }
      return false;
    }, []);
  },
  useIsUserLoggedIn(): boolean {
    const userInfo = messageHooks.useSubjectState(AccountMessages.AccountUserInfo);

    return !!userInfo?.user?.mainProfile;
  },

  useUserData(): UserAccountData {
    const userInfo = messageHooks.useSubjectState(AccountMessages.AccountUserInfo);

    return React.useMemo(() => {
      const userData = userInfo?.user?.mainProfile;

      return {
        userData,
        firstName: strings.convertToString(userData?.firstName),
        lastName: strings.convertToString(userData?.lastName),
        email: strings.convertToString(userData?.email),
        phoneNumber: strings.convertToString(userData?.phoneNumber),
      };
    }, [userInfo]);
  },
  useBackArrowLinkProps(title: string) {
    const redirect = navigationHooks.useAccountOverviewRedirect();
    return {
      title,
      redirect,
    };
  },
};
